/**
 * listingEdit — "Save Changes" on a listing actually saves it.
 *
 * REPORTED 2026-08-31 by a real person: posted an item in the app, opened the
 * listing on the mobile WEBSITE to change details, tapped Save Changes, nothing
 * happened. `#editl-save` is in GUARDED_WRITES (writeGuard.ts) because Blake's
 * handler mutates an in-memory array and toasts "Saved · your changes are live"
 * over zero server calls. The guard turned a lie into a dead end; this is the
 * real write that was missing.
 *
 * ══ 🔴 REWRITTEN 2026-08-31, SAME DAY, BECAUSE THE FIRST VERSION DID NOTHING ══
 *
 * The first version read `window.editModalFor`, `window.LISTINGS` and
 * `window.MY_COLLECTION`. NONE OF THOSE EXIST.
 *
 *     app.js:3629   let editModalFor = null, editlCond = null;
 *     data.js:151   const LISTINGS = [...]
 *
 * data.js and app.js are CLASSIC scripts, and a top-level `let`/`const` is
 * LEXICAL — it lives in script scope, never on `window`. Only `var` and function
 * declarations become window properties. So every one of those reads was
 * `undefined`, `save()` returned at its first guard, and the button stayed
 * exactly as dead as before.
 *
 * 🪤 AND THE TESTS PASSED, WHICH IS THE REAL LESSON. The harness did
 * `win.LISTINGS = [...]` and `win.editModalFor = "l1"` — it CREATED the
 * condition the real bundle does not provide, then asserted against its own
 * invention. Fifteen tests and two mutation controls, all green, all measuring
 * nothing. commentPostLive.test.ts's header already names this exact trap:
 * "our layer behaving correctly against markup we wrote, over a bundle that does
 * otherwise." This file is now tested against the REAL bundle.
 *
 * ══ SO EVERYTHING COMES FROM THE DOM ══
 *
 * Nothing here touches demo script-scope state, because it cannot:
 *
 *   listing id   `[data-ownedit]` on the opener carries it (app.js:175, :987, :3582)
 *   title/value/description  the three inputs
 *   condition    the chip handler does classList.toggle("on", …) (app.js:4096),
 *                so the SELECTION IS VISIBLE as `[data-editlcond].on`
 *
 * The DOM is the one surface both bundles genuinely share.
 *
 * ══ 🔑 CAPTURE PHASE ══
 *
 * The guard listens on `document` and calls stopPropagation(). A bubble-phase
 * listener would never fire and this module would be silently inert. Capture
 * runs first, and because the guard calls stopPropagation() and NOT
 * stopImmediatePropagation(), both run — ours saves, the guard keeps Blake's
 * fabricator dead underneath.
 *
 * ══ IN PLACE, NO RELOAD (card 1219177682942591, 2026-10-17) ══
 *
 * The guard suppresses Blake's save handler ENTIRELY — including the
 * `editModalFor = null; rerenderKeepScroll()` that closes the modal and repaints
 * the card. Until this card the module reloaded the page after a save, so the
 * screen showed what the server holds, at the cost of the whole page. Now:
 *
 *   1. the PATCH is accepted;
 *   2. the listing is RE-READ (GET /api/listings/:id, not cached on the server,
 *      the { listing, seller } envelope) and the page's own row takes the
 *      SERVER's answer through `mapListingRow`, the same mapping the boot uses,
 *      so the price shown is the price a reload would show; the row's own live
 *      state (the thread, its count, Looking For) rides over the save, as the
 *      wizard's `__gbzApplyEdit` does; a re-read that fails leaves the typed
 *      change on the row, because the save landed;
 *   3. the Your Collection tile follows (`__gbzUpdateCollectionItem`);
 *   4. Blake's OWN close handler ends the edit: `#editl-close` is not a guarded
 *      write, and his handler repaints with the scroll kept, so the cards and
 *      the page under the pop-up read the rows this just moved;
 *   5. the toast, after the repaint (a repaint emits a fresh toast node).
 *
 * The rows are reached through the bridge (`__gbzListingRow` hands out the live
 * LISTINGS row), never through window.LISTINGS, which does not exist (above).
 * A "coll-" row (a collection piece shown on the floor) takes the sent patch:
 * PATCH /api/collections/:id echoes none of the edited fields.
 */
import { mapListingRow, collectionStatusFor } from "./floorTransport";
import type { ApiListingRow } from "./floorTransport";
import { SAVED_IN_REVIEW_MESSAGE } from "./wizardPublish";

const OPENER_SELECTOR = "[data-ownedit]";
const SAVE_SELECTOR = "#editl-save";
const CLOSE_SELECTOR = "#editl-close";

export const SAVED_MESSAGE = "Saved · your changes are live";
export const NO_CHANGES_MESSAGE = "No changes to save";
export const SELLER_ONLY_MESSAGE = "Only the seller can edit this listing";
export const SIGNIN_MESSAGE = "Sign in again to save this listing.";
export const OFFLINE_MESSAGE = "Could not save your changes. Check your connection and try again.";
export const FAILED_MESSAGE = "Could not save your changes. Try again.";

export interface ListingFields {
  title: string;
  declaredValue: string;
  condition: string;
  description: string;
}

export interface ListingPatch {
  title?: string;
  declaredValue?: number;
  condition?: string;
  description?: string;
}

/** The floor shows the seller's own collection pieces as rows whose id is
 *  "coll-" plus the collection row's id (floorTransport mapCollectionItemRow).
 *  Quick Edit on one of them saves through the collection route, not the
 *  listing route, which answers 404 for that id. */
export const COLLECTION_ROW_PREFIX = "coll-";

/** The same four fields in the collection route's names. Pure; exported for tests. */
export function collectionPatchFor(patch: ListingPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (patch.title !== undefined) out.title = patch.title;
  if (patch.description !== undefined) out.description = patch.description;
  if (patch.condition !== undefined) out.condition = patch.condition;
  if (patch.declaredValue !== undefined) out.estimatedValue = patch.declaredValue;
  return out;
}

/** Where a Quick Edit save goes, and what it carries, for this row's id. */
export function saveRequestFor(id: string, patch: ListingPatch): { url: string; body: object } {
  if (id.startsWith(COLLECTION_ROW_PREFIX)) {
    return { url: `/api/collections/${encodeURIComponent(id.slice(COLLECTION_ROW_PREFIX.length))}`, body: collectionPatchFor(patch) };
  }
  return { url: `/api/listings/${encodeURIComponent(id)}`, body: patch };
}

/**
 * 🔴 HIS ATTRIBUTE CARRIES THE LABEL, NOT THE VALUE.
 *
 * data.js:147 is the pair list:
 *   CONDITIONS = [["new","New"],["like_new","Like New"],["good","Good"],
 *                 ["fair","Fair"],["poor","Poor"]]
 *
 * and app.js:3830 renders `data-editlcond="${esc(lab)}"` — the LABEL half. So
 * the DOM says "Like New" while the API enum (lib/db conditionEnum) is
 * `like_new`. Posting the raw attribute gets a 400:
 *   Invalid option: expected one of "new"|"like_new"|"good"|"fair"|"poor"
 *
 * Found on staging 2026-09-01 by editing a listing. It had shipped, and the
 * reason it shipped is worth more than the fix:
 *
 * 🪤 THE TEST ASSERTED THE BUG. listingEditLive.test.ts pinned
 * `{ condition: "Like New" }` — my own output — instead of what the server
 * accepts. The harness was HONEST this time: it loaded the real bundle, clicked
 * his real chip, read the real DOM. Only the EXPECTATION was wrong. That is a
 * different failure from the window.* one this file was rewritten to fix, and a
 * quieter one: a mutation control cannot catch it either, because mutating the
 * code turns the test red exactly as designed while both sides stay wrong
 * together. An assertion is only as good as the contract it is derived from,
 * and this one was derived from the code under test.
 *
 * ⚠️ DO NOT normalise by lowercasing and swapping spaces for underscores. It
 * happens to work for these five and would silently invent a value the moment a
 * label like "Brand New" appears. The pairs are the contract; the test derives
 * them from data.js so this map cannot drift from his bundle.
 */
export const CONDITION_LABEL_TO_VALUE: Readonly<Record<string, string>> = Object.freeze({
  New: "new",
  "Like New": "like_new",
  Good: "good",
  Fair: "fair",
  Poor: "poor",
});

/** His label to the API's enum value. Unknown labels return "" so the patch
 *  omits condition entirely rather than sending something the enum refuses. */
export function conditionValueForLabel(label: string): string {
  return CONDITION_LABEL_TO_VALUE[label] ?? "";
}

/** Read the modal's four fields straight out of the DOM. Exported for tests. */
export function readListingFields(doc: Document): ListingFields {
  const val = (id: string): string => {
    const el = doc.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
    return el?.value?.trim() ?? "";
  };
  /* The selected condition is the chip carrying `.on` — app.js:4096 keeps that
     class in sync on every chip click, so the DOM already knows the answer that
     `editlCond` holds in script scope. */
  const chip = doc.querySelector("[data-editlcond].on") as HTMLElement | null;
  return {
    title: val("editl-title"),
    declaredValue: val("editl-value"),
    condition: conditionValueForLabel(chip?.dataset.editlcond ?? ""),
    description: val("editl-desc"),
  };
}

/**
 * Build the PATCH body from what changed. Pure — exported for tests.
 *
 * 🪤 EDITED FIELDS ONLY. Sending every field on every save would turn a title
 * edit into a rewrite of the whole row and clobber a concurrent change made in
 * the app.
 *
 * An emptied field is NOT an edit: the modal cannot produce a titleless listing,
 * and blank is how an untouched-but-cleared input looks. Blank means unchanged.
 */
export function buildListingPatch(initial: ListingFields, next: ListingFields): ListingPatch {
  const patch: ListingPatch = {};
  if (next.title && next.title !== initial.title) patch.title = next.title;
  if (next.description !== initial.description && next.description !== "") {
    patch.description = next.description;
  }
  if (next.condition && next.condition !== initial.condition) patch.condition = next.condition;
  const parsed = Number.parseInt(next.declaredValue, 10);
  if (
    Number.isFinite(parsed) &&
    parsed > 0 &&
    next.declaredValue !== initial.declaredValue
  ) {
    patch.declaredValue = parsed;
  }
  return patch;
}

interface EditWindow extends Window {
  toast?: (message: string) => void;
  /** The live LISTINGS row (the row itself, not a copy). */
  __gbzListingRow?: (id: string) => Record<string, unknown> | null;
  /** One MY_COLLECTION row, updated in place by its id or its listing id. */
  __gbzUpdateCollectionItem?: (sel: { id?: string; lid?: string }, fields: Record<string, unknown>) => boolean;
  rerenderKeepScroll?: () => void;
}

/** The typed patch in the page row's own field names (for a re-read that failed, and for a "coll-" row). */
export function patchAsRow(patch: ListingPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (patch.title !== undefined) out.title = patch.title;
  if (patch.declaredValue !== undefined) out.declaredValue = patch.declaredValue;
  if (patch.condition !== undefined) out.condition = patch.condition;
  if (patch.description !== undefined) out.description = patch.description;
  return out;
}

export function installListingEdit(
  win: EditWindow,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      win.toast?.(message);
    },
    defer = (fn: () => void) => queueMicrotask(fn),
  }: {
    notify?: (m: string) => void;
    defer?: (fn: () => void) => void;
  } = {},
): () => void {
  let busy = false;
  /** Set when the modal opens; the id, the values as they were on open, and
   *  which opening this is: a save's answer that lands after the seller closed
   *  the pop-up and opened another (the same item or not) must not close that
   *  one, nor repaint under their typing. */
  let openSeq = 0;
  let context: { id: string; initial: ListingFields; seq: number } | null = null;

  async function serverError(res: Response, fallback: string): Promise<string> {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return body.error ?? fallback;
  }

  /** The page's rows take the save (see the header): the server's row where it
   *  can be re-read, the typed patch where it cannot, and the closet tile
   *  follows. Returns the row's status as the server now holds it (null when it
   *  could not be read): a save that moves the value over the high-value line
   *  puts the listing in review (listings.ts holdForHighValue), and the tile
   *  and the toast must say so rather than "live". */
  async function applyInPlace(id: string, patch: ListingPatch): Promise<string | null> {
    const live = win.__gbzListingRow?.(id) ?? null;
    if (id.startsWith(COLLECTION_ROW_PREFIX)) {
      if (live) Object.assign(live, patchAsRow(patch));
      const fields: Record<string, unknown> = {};
      if (patch.title !== undefined) fields.title = patch.title;
      if (patch.declaredValue !== undefined) fields.value = patch.declaredValue;
      if (patch.description !== undefined) fields.note = patch.description;
      win.__gbzUpdateCollectionItem?.({ id: id.slice(COLLECTION_ROW_PREFIX.length) }, fields);
      return null;
    }
    let fresh: Record<string, unknown> | null = null;
    let status: string | null = null;
    try {
      const r = await authFetch(`/api/listings/${encodeURIComponent(id)}`);
      if (r.ok) {
        const row = (await r.json()) as Partial<ApiListingRow> | null;
        if (row && row.listing && typeof row.listing === "object") {
          fresh = mapListingRow(row as ApiListingRow).listing as unknown as Record<string, unknown>;
          status = (row.listing as { status?: string | null }).status ?? null;
        }
      }
    } catch {
      fresh = null;
    }
    if (live) {
      if (fresh) {
        /* The page's own live state rides over the save: the thread, its count
           and Looking For are not on the single-listing read, and a save must
           not empty them until the next page load (as __gbzApplyEdit keeps them). */
        const keep = { comments: live.comments, commentCount: live.commentCount, lookingFor: live.lookingFor };
        Object.assign(live, fresh);
        const comments = fresh.comments as unknown[] | undefined;
        const lookingFor = fresh.lookingFor as unknown[] | undefined;
        if ((!comments || !comments.length) && keep.comments) live.comments = keep.comments;
        if (!fresh.commentCount && keep.commentCount) live.commentCount = keep.commentCount;
        if ((!lookingFor || !lookingFor.length) && keep.lookingFor) live.lookingFor = keep.lookingFor;
      } else {
        Object.assign(live, patchAsRow(patch));
      }
    }
    const row = fresh ?? live;
    if (row) {
      win.__gbzUpdateCollectionItem?.({ lid: id }, { title: row.title, value: row.declaredValue, image: row.image });
    } else {
      const fields: Record<string, unknown> = {};
      if (patch.title !== undefined) fields.title = patch.title;
      if (patch.declaredValue !== undefined) fields.value = patch.declaredValue;
      win.__gbzUpdateCollectionItem?.({ lid: id }, fields);
    }
    /* The tile wears the status the server now holds, in the closet's own words
       (the save can put the listing in review: the high-value hold; a listing
       in review can have been approved since the tile was painted). Its terms
       are kept. The page's own row stays where it is: the page under the
       pop-up is that listing's. */
    const word = status ? collectionStatusFor(status) : null;
    if (word) win.__gbzUpdateCollectionItem?.({ lid: id }, { status: word });
    return status;
  }

  /** His own close handler ends the edit and repaints with the scroll kept.
   *  True when the pop-up was there to close. */
  function closePopup(): boolean {
    const close = win.document.querySelector(CLOSE_SELECTOR) as HTMLElement | null;
    if (!close) return false;
    close.click();
    return true;
  }

  /** The pop-up is already gone (the seller closed it while the save was out):
   *  the rows moved, and the page shows them on its next paint. Repaint now
   *  only when nobody is typing, no text box holds text and no drag is in
   *  progress: the same guard wizardPublish's flip uses. (No toast to keep: the
   *  save's own toast follows this at once.) */
  function repaintIfIdle(): void {
    const doc = win.document;
    const active = doc.activeElement as HTMLElement | null;
    const typing = !!active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);
    const holdsText = [...doc.querySelectorAll("#app input, #app textarea")].some((el) => (el as HTMLInputElement).value);
    const dragging = !!doc.querySelector(".mgr-grid.reordering");
    if (typing || holdsText || dragging) return;
    win.rerenderKeepScroll?.();
  }

  async function save(): Promise<void> {
    if (busy) return;
    if (!context) return;
    const { id, initial, seq } = context;

    const patch = buildListingPatch(initial, readListingFields(win.document));

    if (Object.keys(patch).length === 0) {
      /* The person pressed the button. Silence is the exact symptom being fixed,
         so say why nothing happened and close as they expect. */
      notify(NO_CHANGES_MESSAGE);
      (win.document.querySelector(CLOSE_SELECTOR) as HTMLElement | null)?.click();
      return;
    }

    const req = saveRequestFor(id, patch);
    if (Object.keys(req.body).length === 0) {
      /* A patch the collection route cannot carry maps to nothing, and an empty
         body would flip the piece's availability there. Say so, send nothing. */
      notify(NO_CHANGES_MESSAGE);
      (win.document.querySelector(CLOSE_SELECTOR) as HTMLElement | null)?.click();
      return;
    }

    busy = true;
    try {
      const res = await authFetch(req.url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req.body),
      });

      if (res.status === 401) { notify(SIGNIN_MESSAGE); return; }
      if (res.status === 403) {
        /* The route is seller-only. "Try again" is wrong advice for a permission
           problem that will never resolve. */
        notify(SELLER_ONLY_MESSAGE);
        return;
      }
      if (!res.ok) { notify(await serverError(res, FAILED_MESSAGE)); return; }

      /* See the header: the rows take the save, his close repaints, then the
         toast. The close is for THIS opening of the pop-up: a late answer must
         not close one the seller has since opened (another item, or the same
         one again), nor repaint under their typing. */
      const status = await applyInPlace(id, patch);
      const thisOpening = context !== null && context.seq === seq;
      if (!thisOpening || !closePopup()) repaintIfIdle();
      notify(status === "under_review" ? SAVED_IN_REVIEW_MESSAGE : SAVED_MESSAGE);
    } catch {
      notify(OFFLINE_MESSAGE);
    } finally {
      busy = false;
    }
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;

    const opener = target.closest(OPENER_SELECTOR) as HTMLElement | null;
    if (opener) {
      const id = opener.dataset.ownedit;
      if (!id) return;
      /* DEFERRED: we run in CAPTURE, before app.js's own target-phase handler
         has set editModalFor and re-rendered. The modal does not exist yet. A
         microtask lands after that synchronous render, so the fields we snapshot
         are the ones the person is about to edit. */
      defer(() => {
        context = { id, initial: readListingFields(win.document), seq: ++openSeq };
      });
      return;
    }

    if (target.closest(SAVE_SELECTOR)) {
      /* No stopPropagation — the guard must still suppress the fabricator. */
      void save();
    }
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
